import { Activity, Search, TrendingUp, UserPlus, Users } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { adminApi } from "../../../api/client";
import LoadingState from "../../../components/common/LoadingState";
import { useAdminAuth } from "../../../context/AdminAuthContext";
import { useToast } from "../../../context/ToastContext";
import type { SignupRange, UsersDashboard } from "../../../types/api";
import { formatDateTime, formatCompactDate } from "../../../utils/format";

const RANGE_LABELS: Record<SignupRange, string> = {
  today: "Today",
  week: "Week",
  month: "Month",
  year: "Year",
};

const RANGE_TITLES: Record<SignupRange, string> = {
  today: "Sign-ups · today",
  week: "Sign-ups · last 7 days",
  month: "Sign-ups · last 30 days",
  year: "Sign-ups · last 12 months",
};

const RANGE_SUBTITLES: Record<SignupRange, string> = {
  today: "Hourly account creation activity",
  week: "Daily account creation activity",
  month: "Daily account creation activity",
  year: "Monthly account creation activity",
};

function initials(name?: string | null) {
  if (!name) return "?";
  return (
    name
      .split(" ")
      .map((part) => part[0])
      .filter(Boolean)
      .slice(0, 2)
      .join("")
      .toUpperCase() || "?"
  );
}

export default function AdminUsersPage() {
  const { token } = useAdminAuth();
  const { showToast } = useToast();
  const [data, setData] = useState<UsersDashboard | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [range, setRange] = useState<SignupRange>("year");

  useEffect(() => {
    if (!token) return;
    let active = true;

    const load = async () => {
      try {
        const response = await adminApi.listUsers(token, range);
        if (active) {
          setData(response);
          setError(null);
        }
      } catch (requestError) {
        if (active) {
          const message =
            requestError instanceof Error ? requestError.message : "Could not load users";
          setError(message);
          showToast(message, "error");
        }
      }
    };

    void load();
    const intervalId = window.setInterval(() => void load(), 30000);
    return () => {
      active = false;
      window.clearInterval(intervalId);
    };
  }, [range, showToast, token]);

  const filteredUsers = useMemo(() => {
    if (!data) return [];
    const users = data.users ?? [];
    const term = query.trim().toLowerCase();
    if (!term) return users;
    return users.filter((user) => {
      const haystack = `${user.name ?? ""} ${user.email ?? ""} ${user.phone ?? ""}`.toLowerCase();
      return haystack.includes(term);
    });
  }, [data, query]);

  if (!data && !error) return <LoadingState label="Loading users..." />;

  if (error || !data) {
    return (
      <div className="ego-surface px-6 py-12 text-center text-sm font-semibold text-red-600">
        {error ?? "No data"}
      </div>
    );
  }

  const buckets = data.signups?.buckets ?? [];
  const activeRange = data.signups?.range ?? range;
  const maxBar = buckets.reduce((max, bucket) => Math.max(max, bucket.count), 0);
  const totalInRange = buckets.reduce((sum, bucket) => sum + bucket.count, 0);
  const peakBucket = buckets.reduce(
    (best, bucket) => (bucket.count > best.count ? bucket : best),
    { label: "", count: 0, key: "" }
  );

  // For year/month, we want fewer X-axis labels. Skip every Nth.
  const labelStep = activeRange === "month" ? 5 : activeRange === "today" ? 4 : 1;

  const stats = data.stats ?? {
    totalUsers: 0,
    newToday: 0,
    newLast7: 0,
    newLast30: 0,
    newLastYear: 0,
    activeLast30: 0,
  };
  const kpis = [
    {
      label: "Total users",
      value: (stats.totalUsers ?? 0).toLocaleString(),
      icon: Users,
      tint: "bg-[#fff1ea] dark:bg-[rgba(245,158,11,0.14)] text-[#c2410c]",
    },
    {
      label: "New · today",
      value: (stats.newToday ?? 0).toLocaleString(),
      icon: UserPlus,
      tint: "bg-[#ecfdf5] dark:bg-[rgba(16,185,129,0.14)] text-[#047857]",
    },
    {
      label: "New · last 30d",
      value: (stats.newLast30 ?? 0).toLocaleString(),
      icon: TrendingUp,
      tint: "bg-[#eff6ff] dark:bg-[rgba(59,130,246,0.14)] text-[#1d4ed8]",
    },
    {
      label: "New · last year",
      value: (stats.newLastYear ?? 0).toLocaleString(),
      icon: TrendingUp,
      tint: "bg-[#fffbeb] dark:bg-[rgba(245,158,11,0.14)] text-[#b45309]",
    },
    {
      label: "Active · last 30d",
      value: (stats.activeLast30 ?? 0).toLocaleString(),
      icon: Activity,
      tint: "bg-[#f5f3ff] dark:bg-[rgba(139,92,246,0.14)] text-[#6d28d9]",
    },
  ];

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
        {kpis.map((kpi) => {
          const Icon = kpi.icon;
          return (
            <div key={kpi.label} className="ego-surface p-5">
              <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${kpi.tint}`}>
                <Icon size={18} />
              </div>
              <div className="mt-5 text-2xl font-bold tracking-tight text-foreground">{kpi.value}</div>
              <div className="mt-1 text-sm text-muted-foreground">{kpi.label}</div>
            </div>
          );
        })}
      </div>

      <div className="ego-surface overflow-hidden">
        <div className="border-b border-line px-6 py-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h2 className="text-lg font-bold text-foreground">{RANGE_TITLES[activeRange]}</h2>
              <p className="text-sm text-muted-foreground">{RANGE_SUBTITLES[activeRange]}</p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <div className="text-right text-sm">
                <div className="text-xs uppercase tracking-wide text-muted-foreground">Total</div>
                <div className="font-bold text-foreground">{totalInRange}</div>
              </div>
              {peakBucket.label && peakBucket.count > 0 && (
                <div className="text-right text-sm">
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">Peak</div>
                  <div className="font-bold text-foreground">
                    {peakBucket.count} · {peakBucket.label}
                  </div>
                </div>
              )}
              <div className="flex flex-wrap gap-1 rounded-xl bg-surface-muted p-1">
                {(Object.keys(RANGE_LABELS) as SignupRange[]).map((value) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setRange(value)}
                    className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition ${
                      range === value
                        ? "bg-surface text-foreground shadow-sm"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {RANGE_LABELS[value]}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="px-6 pb-6 pt-6">
          {totalInRange === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground">
              No sign-ups in this range yet.
            </div>
          ) : (
            <>
              <div className="flex h-36 items-end gap-1.5">
                {buckets.map((bucket) => {
                  const heightPct = maxBar > 0 ? (bucket.count / maxBar) * 100 : 0;
                  return (
                    <div
                      key={bucket.key}
                      className="group relative flex-1"
                      title={`${bucket.label} · ${bucket.count} signup${bucket.count === 1 ? "" : "s"}`}
                    >
                      <div
                        className="relative w-full rounded-t-md bg-[var(--pp-gold)] opacity-80 transition group-hover:opacity-100"
                        style={{ height: `${Math.max(heightPct, bucket.count > 0 ? 4 : 1)}%` }}
                      >
                        {bucket.count > 0 && (
                          <span className="absolute -top-5 left-1/2 -translate-x-1/2 text-[10px] font-bold text-foreground">
                            {bucket.count}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-2 flex">
                {buckets.map((bucket, index) => (
                  <div key={bucket.key} className="flex-1 truncate text-center text-[10px] text-[#9aa3b2]">
                    {index % labelStep === 0 ? bucket.label : ""}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      <div className="ego-surface flex flex-col gap-3 p-5 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9aa3b2]" />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search by name, email, or phone"
            className="ego-input pl-9"
          />
        </div>
        <div className="text-sm text-muted-foreground">
          {filteredUsers.length} of {data.users.length}
        </div>
      </div>

      <div className="ego-surface overflow-hidden">
        <div className="hidden grid-cols-[1.4fr_1.2fr_0.7fr_0.7fr_0.9fr] gap-4 border-b border-line bg-surface-muted px-6 py-3 text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground lg:grid">
          <div>Customer</div>
          <div>Contact</div>
          <div>Orders</div>
          <div>Last order</div>
          <div>Joined</div>
        </div>
        {filteredUsers.length === 0 ? (
          <div className="px-6 py-16 text-center text-sm text-muted-foreground">No users found.</div>
        ) : (
          <div className="ego-divide">
            {filteredUsers.map((user) => (
              <div
                key={user.id}
                className="grid gap-3 px-6 py-4 transition hover:bg-surface-muted lg:grid-cols-[1.4fr_1.2fr_0.7fr_0.7fr_0.9fr] lg:items-center"
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#fff1ea] dark:bg-[rgba(245,158,11,0.14)] text-xs font-bold text-[#c2410c]">
                    {initials(user.name)}
                  </div>
                  <div className="min-w-0">
                    <div className="truncate text-sm font-semibold text-foreground">
                      {user.name || "Unnamed customer"}
                    </div>
                    {user.lastActiveAt && (
                      <div className="text-[11px] text-[#9aa3b2]">
                        Last seen {formatDateTime(user.lastActiveAt)}
                      </div>
                    )}
                  </div>
                </div>
                <div className="min-w-0">
                  <div className="truncate text-sm text-foreground">{user.email || "—"}</div>
                  <div className="text-xs text-muted-foreground">{user.phone || "—"}</div>
                </div>
                <div className="text-sm font-semibold text-foreground">{user.ordersCount}</div>
                <div className="text-xs text-muted-foreground">
                  {user.lastOrderAt ? formatDateTime(user.lastOrderAt) : "—"}
                </div>
                <div className="text-xs text-muted-foreground">
                  {user.createdAt ? formatCompactDate(user.createdAt) : "—"}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
